import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { QRCodeSVG } from 'qrcode.react';
import { StudentDetails } from './types';




interface ViewStudentModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string | null;
}

const ViewStudentModal: React.FC<ViewStudentModalProps> = ({ open, onClose, studentId }) => {
  const toast = useRef<Toast>(null);
  const [studentData, setStudentData] = useState<StudentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setProfileImageUrl(null);
      setStudentData(null);
      return;
    }

    const fetchStudentData = async () => {
      if (!studentId) return;

      setLoading(true);
      setIsClosing(false);
      setProfileImageUrl(null);

      try {
        const { fetchStudentDetails } = await import('../../../stateManagers/services/fetchStudentDetails');
        const data = await fetchStudentDetails(studentId);
        
        // Cast the data to match our extended interface
        setStudentData(data as StudentDetails);
        
        setProfileImageUrl(data?.profile_picture_url
          ? `${data.profile_picture_url}?v=${Date.now()}`
          : null);
      } catch (error) {
        if (error instanceof Error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to fetch student details',
            life: 5000
          });
        }
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [open, studentId, onClose]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setProfileImageUrl(null);
    setStudentData(null);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const InfoRow = ({
    icon,
    label,
    value,
    isChip = false,
    chipColor = '#4169E1'
  }: {
    icon: string;
    label: string;
    value: string | number;
    isChip?: boolean;
    chipColor?: string;
  }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '36px', height: '36px', borderRadius: '10px',
        background: 'rgba(65, 105, 225, 0.08)', color: '#4169E1', flexShrink: 0
      }}>
        <i className={icon} style={{ fontSize: '15px' }}></i>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '11px', fontWeight: 700, color: '#6b7280',
          textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px'
        }}>
          {label}
        </div>
        {isChip ? (
          <span style={{
            display: 'inline-block', padding: '5px 12px', borderRadius: '8px',
            background: chipColor, color: 'white', fontSize: '13px', fontWeight: 600,
            boxShadow: `0 2px 8px ${chipColor}40`
          }}>
            {value}
          </span>
        ) : (
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', lineHeight: 1.3 }}>
            {value}
          </div>
        )}
      </div>
    </div>
  );

  const headerTemplate = () => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      gap: '16px', 
      padding: '8px 0',
      width: '100%'
    }}>
      {/* Left side - Profile and Name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
        <div style={{ position: 'relative' }}>
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt="Student Profile"
              style={{
                width: '80px', height: '80px', borderRadius: '50%',
                objectFit: 'cover', border: '3px solid white',
                boxShadow: '0 8px 24px rgba(65, 105, 225, 0.25)'
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                const sibling = (e.target as HTMLImageElement).nextElementSibling;
                if (sibling instanceof HTMLElement) sibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div style={{
            width: '80px', height: '80px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
            display: profileImageUrl ? 'none' : 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '32px', fontWeight: 700,
            boxShadow: '0 8px 24px rgba(65, 105, 225, 0.3)',
            flexShrink: 0, border: '3px solid white'
          }}>
            {studentData?.name?.charAt(0).toUpperCase() || 'S'}
          </div>
          <svg style={{
            position: 'absolute', top: '-4px', left: '-4px',
            width: '88px', height: '88px', transform: 'rotate(-90deg)', pointerEvents: 'none'
          }}>
            <circle
              cx="44" cy="44" r="41" fill="none"
              stroke="url(#gradientRing)" strokeWidth="2.5"
              strokeDasharray="258" strokeDashoffset="64.5" strokeLinecap="round"
              style={{ filter: 'drop-shadow(0 0 4px rgba(65, 105, 225, 0.4))' }}
            />
            <defs>
              <linearGradient id="gradientRing" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4169E1" />
                <stop offset="50%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
            {studentData?.name || 'Student Profile'}
          </div>
        </div>
      </div>

      {/* Right side - QR Code */}
      {studentData?.id && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}>
          <QRCodeSVG
            value={studentData.id.toString()}
            size={64}
            fgColor="#1e40af"
            bgColor="#ffffff"
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '800px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)', overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none'
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px'
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important; }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241, 245, 249, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }
        `}</style>

        {loading ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '80px 20px', gap: '16px'
          }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>
              Loading student details...
            </span>
          </div>
        ) : studentData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Personal Information */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '16px 18px',
              border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '14px', paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)', color: 'white'
                }}>
                  <i className="pi pi-user" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{ color: '#1e40af', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  Personal Information
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow icon="pi pi-user" label="Full Name" value={studentData.name} />
                <InfoRow icon="pi pi-users" label="Gender" value={studentData.gender} />
                <InfoRow icon="pi pi-calendar" label="Date of Birth" value={studentData.dob} />
                <InfoRow icon="pi pi-heart" label="Blood Group" value={studentData.blood_group} />
              </div>
            </div>

            {/* Academic Information */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px 18px',
              border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '18px', paddingBottom: '14px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)', color: 'white'
                }}>
                  <i className="pi pi-book" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{ color: '#1e40af', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  Academic Information
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '8px' }}>
                <InfoRow icon="pi pi-graduation-cap" label="Class" value={studentData.class_name} />
                {studentData.section && studentData.section !== 'N/A' && (
                  <InfoRow icon="pi pi-th-large" label="Section" value={studentData.section} />
                )}
                <InfoRow icon="pi pi-id-card" label="Admission Number" value={studentData.admission_no} />
                <InfoRow icon="pi pi-hashtag" label="Roll Number" value={studentData.roll_no} />
                <InfoRow icon="pi pi-star" label="Admission Type" value={studentData.admission_type} />
              </div>
            </div>

            {/* Contact & Parent Information */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px 18px',
              border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '18px', paddingBottom: '14px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)', color: 'white'
                }}>
                  <i className="pi pi-phone" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{ color: '#1e40af', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  Contact & Parent Information
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <InfoRow icon="pi pi-envelope" label="Email Address" value={studentData.email_id} />
                <InfoRow icon="pi pi-map-marker" label="Address" value={studentData.address} />
                <div style={{ borderTop: '1px solid rgba(229, 231, 235, 0.5)', margin: '12px 0 8px', paddingTop: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '8px' }}>
                    <InfoRow icon="pi pi-user" label="Father's Name" value={studentData.father_name} />
                    <InfoRow icon="pi pi-phone" label="Father's Contact" value={studentData.father_no} />
                    <InfoRow icon="pi pi-user" label="Mother's Name" value={studentData.mother_name} />
                    <InfoRow icon="pi pi-phone" label="Mother's Contact" value={studentData.mother_no} />
                  </div>
                </div>
              </div>
            </div>

          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default ViewStudentModal;